import { IPomReportingTableData } from "./pom-reporting-table-data";

/** BC resolves prices per unit down to 0,00002 €, so fewer than five decimals renders them as zero. */
const UNIT_PRICE_DECIMALS = 5;
const MONEY_DECIMALS = 2;
const NO_PRICE_PLACEHOLDER = "—";

const format = (value: number, decimals: number): string => {
  const amount = value.toLocaleString("de-DE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${amount} €`;
};

export const formatUnitPrice = (value: number): string => format(value, UNIT_PRICE_DECIMALS);

export const formatMoney = (value: number): string => format(value, MONEY_DECIMALS);

export const lineTotal = (quantity: number, unitPrice: number | undefined): number | undefined => {
  return unitPrice === undefined ? undefined : quantity * unitPrice;
};

export const effectiveQuantity = (position: IPomReportingTableData): number => {
  return position.editedQuantity ?? position.currentQuantity;
};

export const formatUnitPriceOrPlaceholder = (unitPrice: number | undefined): string => {
  return unitPrice === undefined ? NO_PRICE_PLACEHOLDER : formatUnitPrice(unitPrice);
};

export const formatLineTotal = (position: IPomReportingTableData): string => {
  const total = lineTotal(effectiveQuantity(position), position.unitPrice);
  return total === undefined ? NO_PRICE_PLACEHOLDER : formatMoney(total);
};

/** Compares in cents, since edits that cancel out can leave float noise that would render as "0,00 €". */
export const hasVisibleChange = (changeTotal: number): boolean => {
  return Math.round(changeTotal * 10 ** MONEY_DECIMALS) !== 0;
};

export interface IPomReportingPeriodTotals {
  /** Net value of the period as it would stand after submitting. */
  total: number;
  /** Net value of the pending edits alone; negative when quantities were corrected downwards. */
  changeTotal: number;
  unpricedCount: number;
}

/** Totals stay unrounded and are rounded once when rendered, so sub-cent lines still add up. */
export const periodTotals = (positions: IPomReportingTableData[]): IPomReportingPeriodTotals => {
  let total = 0;
  let changeTotal = 0;
  let unpricedCount = 0;

  for (const position of positions) {
    const quantity = effectiveQuantity(position);

    if (position.unitPrice === undefined) {
      // A position at zero before and after the edit adds nothing, so its missing price leaves nothing out
      if (quantity !== 0 || position.currentQuantity !== 0) {
        unpricedCount++;
      }
      continue;
    }

    total += quantity * position.unitPrice;
    changeTotal += (quantity - position.currentQuantity) * position.unitPrice;
  }

  return { total, changeTotal, unpricedCount };
};

export const hasAnyPrice = (positions: IPomReportingTableData[]): boolean => {
  return positions.some((position) => position.unitPrice !== undefined);
};
